import { useEffect, useMemo, useState } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { supabase } from '../../services/supabase'
import { useSimpleHqAccess } from './useSimpleHqAccess'
import { formatShortDate, overlapsDates } from './hqUtils'
import { currentLeaveYear } from './constants'

type LeaveRow = {
  id: string
  user_id: string
  start_date: string
  end_date: string
  days: number
  status: string
  leave_type_id: string
  reason: string | null
}

type RosterRow = { user_id: string; email: string }

export function LeaveApprovalsView() {
  const { user } = useAuth()
  const { companyId } = useSimpleHqAccess()
  const [rows, setRows] = useState<LeaveRow[]>([])
  const [allApproved, setAllApproved] = useState<LeaveRow[]>([])
  const [types, setTypes] = useState<Record<string, string>>({})
  const [roster, setRoster] = useState<RosterRow[]>([])
  const [balances, setBalances] = useState<Record<string, number>>({})
  const [busyId, setBusyId] = useState<string | null>(null)
  const [note, setNote] = useState<Record<string, string>>({})

  const load = async () => {
    const [{ data: pending }, { data: approved }, { data: t }, { data: r }] = await Promise.all([
      supabase
        .from('leave_requests')
        .select('id, user_id, start_date, end_date, days, status, leave_type_id, reason')
        .eq('company_id', companyId)
        .eq('status', 'pending')
        .order('start_date'),
      supabase
        .from('leave_requests')
        .select('id, user_id, start_date, end_date, days, status, leave_type_id, reason')
        .eq('company_id', companyId)
        .eq('status', 'approved'),
      supabase.from('leave_types').select('id, name').eq('company_id', companyId),
      supabase.rpc('simple_hq_roster'),
    ])
    setRows((pending as LeaveRow[]) || [])
    setAllApproved((approved as LeaveRow[]) || [])
    const tm: Record<string, string> = {}
    for (const x of t || []) tm[x.id] = x.name
    setTypes(tm)
    setRoster((r as RosterRow[]) || [])

    const bal: Record<string, number> = {}
    const year = currentLeaveYear()
    await Promise.all(
      ((pending as LeaveRow[]) || []).map(async (p) => {
        const { data } = await supabase.rpc('leave_remaining_days', {
          p_user_id: p.user_id,
          p_leave_type_id: p.leave_type_id,
          p_leave_year: year,
        })
        bal[p.id] = typeof data === 'number' ? data : Number(data)
      }),
    )
    setBalances(bal)
  }

  useEffect(() => {
    void load()
  }, [companyId])

  const emailById = useMemo(() => {
    const m: Record<string, string> = {}
    for (const r of roster) m[r.user_id] = r.email
    return m
  }, [roster])

  const conflictsFor = (row: LeaveRow) =>
    allApproved.filter(
      (a) =>
        a.user_id !== row.user_id &&
        overlapsDates(row.start_date, row.end_date, a.start_date, a.end_date),
    )

  const decide = async (id: string, status: 'approved' | 'denied') => {
    if (!user?.id) return
    setBusyId(id)
    try {
      const { error } = await supabase
        .from('leave_requests')
        .update({
          status,
          decided_by: user.id,
          decided_at: new Date().toISOString(),
          decision_note: note[id] || null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .eq('status', 'pending')
      if (error) throw error
      await supabase.functions.invoke('simple-hq-notify', {
        body: { kind: 'leave_decided', leaveRequestId: id, decision: status },
      })
      await load()
    } catch (ex: any) {
      alert(ex.message || 'Failed')
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div>
      <p className="hq-eyebrow">Leave</p>
      <h1 className="hq-title">Approvals</h1>
      <p className="hq-sub">Conflict radar shows who else is already out on overlapping dates.</p>

      {rows.length === 0 ? (
        <div className="hq-panel">
          <p className="hq-empty">No pending approvals.</p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {rows.map((row) => {
            const conflicts = conflictsFor(row)
            const rem = balances[row.id]
            return (
              <article key={row.id} className="hq-panel">
                <div className="flex flex-wrap justify-between gap-2">
                  <div>
                    <strong>{emailById[row.user_id] || row.user_id}</strong>
                    <div className="text-sm text-[var(--hq-muted)]">
                      {types[row.leave_type_id] || 'Leave'} · {formatShortDate(row.start_date)}–
                      {formatShortDate(row.end_date)} · {row.days} day(s)
                    </div>
                    {row.reason && <p className="text-sm mt-1">{row.reason}</p>}
                  </div>
                  <span className="hq-badge hq-badge-pending">Pending</span>
                </div>

                {rem != null && (
                  <div className="hq-ok-box">
                    Balance before approve: {rem} day(s) · after: {rem - Number(row.days)}
                  </div>
                )}

                {conflicts.length > 0 ? (
                  <div className="hq-warn-box">
                    <strong>Conflict radar:</strong>{' '}
                    {conflicts
                      .map(
                        (c) =>
                          `${emailById[c.user_id]?.split('@')[0] || 'teammate'} (${formatShortDate(c.start_date)}–${formatShortDate(c.end_date)})`,
                      )
                      .join('; ')}
                  </div>
                ) : (
                  <div className="hq-ok-box">No overlapping approved leave on the team.</div>
                )}

                <div className="hq-field mt-2">
                  <label htmlFor={`note-${row.id}`}>Decision note</label>
                  <input
                    id={`note-${row.id}`}
                    value={note[row.id] || ''}
                    onChange={(e) => setNote((n) => ({ ...n, [row.id]: e.target.value }))}
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="hq-btn hq-btn-primary"
                    disabled={busyId === row.id}
                    onClick={() => void decide(row.id, 'approved')}
                  >
                    Approve
                  </button>
                  <button
                    type="button"
                    className="hq-btn hq-btn-danger"
                    disabled={busyId === row.id}
                    onClick={() => void decide(row.id, 'denied')}
                  >
                    Deny
                  </button>
                </div>
              </article>
            )
          })}
        </div>
      )}
    </div>
  )
}
